import { c as _c } from "react/compiler-runtime";
import { useRequestErrorToast } from '@onefootprint/hooks';
import request from '@onefootprint/request';
import { useToast } from '@onefootprint/ui';
import { useQueryClient } from '@tanstack/react-query';
import isUndefined from 'lodash/isUndefined';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { clearWatchedRuns } from '@/components/agent-chat/components/run-completion-watcher/watched-runs';
import { resetAutoDecryptStores } from '@/entity/hooks/use-auto-decrypt-controls';
import { clearAllPlaybookEditSessions } from '@/hooks/use-in-progress-edit-session-storage';
import { DASHBOARD_ALLOW_ASSUMED_WRITES, DASHBOARD_AUTHORIZATION_HEADER, DASHBOARD_IS_LIVE_HEADER } from '../../config/constants';
import { getOrgMemberRequest } from '../use-get-org-member/use-get-org-member';
import type { AuthHeaders, MetaSession, OrgSession, Session, UserSession, UserSessionState } from './user-session.types';
import { defaultSession } from './user-session.types';
const logoutUser = (auth: string) => request({
  headers: {
    [DASHBOARD_AUTHORIZATION_HEADER]: auth
  },
  method: 'POST',
  url: '/org/auth/logout'
});
export const useStore = create<UserSessionState>()(persist(set => ({
  data: defaultSession,
  reset: () => set({
    data: defaultSession
  }),
  update: (data: Partial<Session>) => {
    set(oldSession => ({
      ...oldSession,
      data: {
        ...oldSession.data,
        ...data
      }
    }));
  }
}), {
  version: 13,
  name: 'dashboard-storage'
}));
type FetchNewPermissionsParams = {
  newAuthToken: string;
  newIsLive: boolean;
  newIsAssumedSessionEditMode?: boolean;
};
const useSession = () => {
  const $ = _c(64);
  const queryClient = useQueryClient();
  const {
    toast
  } = useToast();
  const showRequestErrorToast = useRequestErrorToast();
  const {
    data,
    reset,
    update
  } = useStore(_temp);
  const t0 = data.auth as string;
  const t1 = data.user as UserSession;
  const t2 = data.org as OrgSession;
  let t3;
  if ($[0] !== data.meta || $[1] !== t0 || $[2] !== t1 || $[3] !== t2) {
    t3 = {
      auth: t0,
      user: t1,
      org: t2,
      meta: data.meta
    };
    $[0] = data.meta;
    $[1] = t0;
    $[2] = t1;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const dangerouslyCastedData = t3;
  const isLoggedIn = !!data.user;
  const isLive = !!data.org?.isLive;
  const isAssumedSessionEditMode = !!data.user?.isAssumedSessionEditMode;
  const t4 = data?.auth as string;
  let t5;
  if ($[5] !== isLive) {
    t5 = JSON.stringify(isLive);
    $[5] = isLive;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let authHeaders;
  if ($[7] !== isAssumedSessionEditMode || $[8] !== t4 || $[9] !== t5) {
    authHeaders = {
      [DASHBOARD_AUTHORIZATION_HEADER]: t4,
      [DASHBOARD_IS_LIVE_HEADER]: t5
    } as AuthHeaders;
    if (isAssumedSessionEditMode) {
      let t6;
      if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
        t6 = JSON.stringify(true);
        $[11] = t6;
      } else {
        t6 = $[11];
      }
      authHeaders[DASHBOARD_ALLOW_ASSUMED_WRITES] = t6;
    }
    $[7] = isAssumedSessionEditMode;
    $[8] = t4;
    $[9] = t5;
    $[10] = authHeaders;
  } else {
    authHeaders = $[10];
  }
  let fetchNewPermissions;
  let fetchNewPermissionsOrLogout;
  let logIn;
  let logOut;
  if ($[12] !== data.auth || $[13] !== data.org?.isLive || $[14] !== isAssumedSessionEditMode || $[15] !== queryClient || $[16] !== reset || $[17] !== showRequestErrorToast || $[18] !== toast || $[19] !== update) {
    logIn = async session => {
      if (session.meta) {
        update({
          meta: session.meta
        });
      }
      await fetchNewPermissionsOrLogout({
        newAuthToken: session.auth,
        newIsLive: session.newIsLive ?? data.org?.isLive ?? true,
        newIsAssumedSessionEditMode: false
      });
      resetAutoDecryptStores();
    };
    if ($[24] !== isAssumedSessionEditMode || $[25] !== queryClient || $[26] !== toast || $[27] !== update) {
      fetchNewPermissions = async t6 => {
        const {
          newAuthToken,
          newIsLive,
          newIsAssumedSessionEditMode
        } = t6;
        const isAssumedSessionEditModeToSet = isUndefined(newIsAssumedSessionEditMode) ? isAssumedSessionEditMode : newIsAssumedSessionEditMode;
        const previousUser = useStore.getState().data.user;
        const user = await getOrgMemberRequest({
          auth: newAuthToken,
          isLive: newIsLive,
          isAssumedSessionEditMode: !!isAssumedSessionEditModeToSet
        });
        if (user.tenant.isSandboxRestricted && newIsLive) {
          toast({
            title: "Switched to sandbox mode",
            description: "This organization doesn't have live mode enabled."
          });
          await fetchNewPermissions({
            newAuthToken,
            newIsLive: false,
            newIsAssumedSessionEditMode: isAssumedSessionEditModeToSet
          });
          return;
        }
        const currentUser = useStore.getState().data.user;
        const preservePreviews = currentUser?.id === user.id && (queryClient.isMutating({
          mutationKey: ["feature-preview", user.id]
        }) > 0 || currentUser.featurePreviews !== previousUser?.featurePreviews);
        update({
          auth: newAuthToken,
          user: {
            ...user,
            featurePreviews: preservePreviews ? currentUser.featurePreviews : user.featurePreviews,
            isAssumedSession: !!user.isAssumedSession,
            isAssumedSessionEditMode: isAssumedSessionEditModeToSet
          },
          org: {
            ...user.tenant,
            isLive: newIsLive
          }
        });
      };
      $[24] = isAssumedSessionEditMode;
      $[25] = queryClient;
      $[26] = toast;
      $[27] = update;
      $[28] = fetchNewPermissions;
    } else {
      fetchNewPermissions = $[28];
    }
    fetchNewPermissionsOrLogout = async params => {
      ;
      try {
        await fetchNewPermissions(params);
      } catch (t6) {
        const error = t6;
        logOut();
        showRequestErrorToast(error);
      }
    };
    logOut = () => {
      if (data.auth) {
        logoutUser(data.auth).catch(console.error);
        resetAutoDecryptStores();
      }
      clearAllPlaybookEditSessions();
      clearWatchedRuns();
      reset();
    };
    $[12] = data.auth;
    $[13] = data.org?.isLive;
    $[14] = isAssumedSessionEditMode;
    $[15] = queryClient;
    $[16] = reset;
    $[17] = showRequestErrorToast;
    $[18] = toast;
    $[19] = update;
    $[20] = fetchNewPermissions;
    $[21] = fetchNewPermissionsOrLogout;
    $[22] = logIn;
    $[23] = logOut;
  } else {
    fetchNewPermissions = $[20];
    fetchNewPermissionsOrLogout = $[21];
    logIn = $[22];
    logOut = $[23];
  }
  let t6;
  if ($[29] !== data.user || $[30] !== update) {
    t6 = nextUser => {
      if (!data.user) {
        return;
      }
      update({
        user: {
          ...data.user,
          ...nextUser
        }
      });
    };
    $[29] = data.user;
    $[30] = update;
    $[31] = t6;
  } else {
    t6 = $[31];
  }
  const updateUserName = t6;
  let t7;
  if ($[32] !== data.org || $[33] !== update) {
    t7 = nextOrg => {
      if (!data.org) {
        return;
      }
      update({
        org: {
          ...data.org,
          ...nextOrg
        }
      });
    };
    $[32] = data.org;
    $[33] = update;
    $[34] = t7;
  } else {
    t7 = $[34];
  }
  const setOrg = t7;
  let t8;
  if ($[35] !== data.auth || $[36] !== fetchNewPermissionsOrLogout || $[37] !== isLive) {
    t8 = async newEditMode => {
      if (!data?.auth) {
        return;
      }
      await fetchNewPermissionsOrLogout({
        newAuthToken: data.auth,
        newIsLive: isLive,
        newIsAssumedSessionEditMode: newEditMode
      });
    };
    $[35] = data.auth;
    $[36] = fetchNewPermissionsOrLogout;
    $[37] = isLive;
    $[38] = t8;
  } else {
    t8 = $[38];
  }
  const setAssumedSessionEditMode = t8;
  let t9;
  if ($[39] !== data.auth || $[40] !== fetchNewPermissionsOrLogout) {
    t9 = async newIsLive_0 => {
      if (!data.auth) {
        return;
      }
      await fetchNewPermissionsOrLogout({
        newAuthToken: data.auth,
        newIsLive: newIsLive_0
      });
    };
    $[39] = data.auth;
    $[40] = fetchNewPermissionsOrLogout;
    $[41] = t9;
  } else {
    t9 = $[41];
  }
  const setIsLive = t9;
  let t10;
  if ($[42] !== data.meta || $[43] !== update) {
    t10 = () => {
      update({
        meta: {
          ...data.meta,
          requiresOnboarding: false
        }
      });
    };
    $[42] = data.meta;
    $[43] = update;
    $[44] = t10;
  } else {
    t10 = $[44];
  }
  const completeOnboarding = t10;
  let t11;
  if ($[45] !== data.auth || $[46] !== fetchNewPermissions || $[47] !== isLive) {
    t11 = async () => {
      if (!data.auth) {
        return;
      }
      await fetchNewPermissions({
        newAuthToken: data.auth,
        newIsLive: isLive
      });
    };
    $[45] = data.auth;
    $[46] = fetchNewPermissions;
    $[47] = isLive;
    $[48] = t11;
  } else {
    t11 = $[48];
  }
  const refreshPermissions = t11;
  let t12;
  if ($[49] !== authHeaders || $[50] !== completeOnboarding || $[51] !== dangerouslyCastedData || $[52] !== data || $[53] !== isAssumedSessionEditMode || $[54] !== isLive || $[55] !== isLoggedIn || $[56] !== logIn || $[57] !== logOut || $[58] !== refreshPermissions || $[59] !== setAssumedSessionEditMode || $[60] !== setIsLive || $[61] !== setOrg || $[62] !== updateUserName) {
    t12 = {
      authHeaders,
      dangerouslyCastedData,
      data,
      isLoggedIn,
      logIn,
      logOut,
      refreshPermissions,
      setOrg,
      updateUserName,
      isLive,
      setIsLive,
      isAssumedSessionEditMode,
      setAssumedSessionEditMode,
      completeOnboarding
    };
    $[49] = authHeaders;
    $[50] = completeOnboarding;
    $[51] = dangerouslyCastedData;
    $[52] = data;
    $[53] = isAssumedSessionEditMode;
    $[54] = isLive;
    $[55] = isLoggedIn;
    $[56] = logIn;
    $[57] = logOut;
    $[58] = refreshPermissions;
    $[59] = setAssumedSessionEditMode;
    $[60] = setIsLive;
    $[61] = setOrg;
    $[62] = updateUserName;
    $[63] = t12;
  } else {
    t12 = $[63];
  }
  return t12;
};
export default useSession;
function _temp(state) {
  return state;
}